<script lang="ts">
  import { listNodes } from '$db/local/repositories';
  import type { Node } from '$domain/types';
  import type { SessionContext } from '$features/session';
  import { SearchSelect } from '$ui/primitives/SearchSelect';

  let {
    value,
    onchange,
    disabled = false,
    size = 'md'
  }: {
    value: SessionContext | null;
    onchange: (patch: Partial<SessionContext>) => void;
    disabled?: boolean;
    size?: 'sm' | 'md';
    showAllContext?: boolean;
    onMutate?: () => void;
    // Props heredadas que ignoramos para mantener compatibilidad
    showSubjectActions?: boolean;
    onSubjectSettings?: () => void;
    onSubjectArchive?: () => void;
    archivedCount?: number;
    onOpenArchived?: () => void;
    onOpenRetention?: (level: any, id: string, name: string) => void;
    onRequestDelete?: (level: any, id: string, name: string) => void;
  } = $props();

  let nodes = $state<Node[]>([]);
  let loading = $state(true);

  $effect(() => {
    listNodes().then((n) => {
      nodes = n;
      loading = false;
    });
  });

  // Si value tiene una ruta guardada, la usamos. Si no, array vacío.
  let activePath = $derived(value?.path || []);

  // Construimos los niveles de dropdowns dinámicamente
  let dropdowns = $derived.by(() => {
    const result: Array<{ options: Node[], selectedId: string | null, depth: number }> = [];
    let currentParentId: string | null = null;
    let depth = 0;

    while (true) {
      const options = nodes.filter(n => n.parentId === currentParentId);
      
      // Si estamos en el primer nivel y no hay nodos, mostramos el vacío
      if (options.length === 0 && depth > 0) break;

      const selectedId = activePath[depth] || null;
      result.push({ options, selectedId, depth });

      if (!selectedId) break; // El usuario aún no ha seleccionado este nivel

      currentParentId = selectedId;
      depth++;
    }
    return result;
  });

  function handleSelect(depth: number, newId: string | null) {
    const newPath = activePath.slice(0, depth);
    if (newId) newPath.push(newId);

    // El nodo seleccionado es el último de la ruta
    const newNodeId = newPath.length > 0 ? newPath[newPath.length - 1] : null;

    onchange({ nodeId: newNodeId, path: newPath });
  }
</script>

<div class="context-fields">
  {#if loading}
    <div class="skeleton-loader">Loading...</div>
  {:else if dropdowns.length === 0 || (dropdowns[0]?.options.length === 0)}
    <p class="empty-msg">No subjects yet. Create one in the Syllabus.</p>
  {:else}
    {#each dropdowns as drop (drop.depth)}
      <div class="field">
        <SearchSelect
          value={drop.selectedId}
          options={drop.options.map(n => ({ value: n.id, label: n.title }))}
          placeholder={drop.depth === 0 ? "Choose a subject" : "Choose..."}
          {size}
          {disabled}
          onchange={(v) => handleSelect(drop.depth, v)}
        />
      </div>
    {/each}
  {/if}
</div>

<style>
  .context-fields {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-2);
    width: 100%;
  }

  .field {
    flex: 0 1 auto;
    min-width: 140px;
  }



  .empty-msg {
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    margin: 0;
  }

  .skeleton-loader {
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }
</style>